// The Bio's HOPS block on ?live=1 (Ben, 2026-10-18 — as Catalyst's Bio): the rating, its percentile
// among every player of the same gender, and the distribution curve with her place on it (the
// curve up to her rating lit green). The data is the server's HOPS distribution (one per gender,
// asked once a page); the maths is hops-curve.ts.

import { useId } from "react";
import type { HopsDistribution } from "@catbento/shared";
import { liveApi } from "../live/api.js";
import { useLiveQuery } from "../live/query.js";
import { buildSmoothHopsCurve, computePercentile, ordinal, parseDistribution } from "./hops-curve.js";

const W = 300;
const H = 64;

export function BioHops({ rating, womens }: { rating: number; womens: boolean }) {
  const raw = useLiveQuery(`hops:distribution:${womens}`, () => liveApi().get<HopsDistribution>(`/hops-distribution?isWomen=${womens}`));
  const clip = useId();
  const dist = parseDistribution(raw);
  const percentile = dist ? computePercentile(rating, dist.percentiles) : null;
  const curve = dist ? buildSmoothHopsCurve(dist.buckets, rating, W, H) : null;
  const end = (v: number) => {
    const p = dist ? computePercentile(v, dist.percentiles) : null;
    return `${(v * 100).toFixed(1)}${p === null ? "" : ` (P${p})`}`;
  };

  return (
    <div className="bio-rating bio-hops">
      <div className="bio-rating-label">
        <span>HOPS Rating</span>
        <span>
          <span className="bio-rating-value">{(rating * 100).toFixed(1)}</span>
          {percentile !== null && <span className="bio-hops-pct"> · {ordinal(percentile)} percentile</span>}
        </span>
      </div>
      {curve ? (
        <>
          <svg className="bio-hops-curve" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="HOPS distribution">
            <defs>
              <clipPath id={clip}>
                <rect x="0" y="0" width={curve.indicatorX} height={H} />
              </clipPath>
              {/* The lit part fades up to her rating, as Catalyst's does (in the site's green). */}
              <linearGradient id={`${clip}-g`} gradientUnits="userSpaceOnUse" x1="0" x2={curve.indicatorX} y1="0" y2="0">
                <stop offset="0" className="bio-hops-g0" />
                <stop offset="1" className="bio-hops-g1" />
              </linearGradient>
            </defs>
            <path className="bio-hops-area" d={curve.areaPath} />
            <path d={curve.areaPath} fill={`url(#${clip}-g)`} clipPath={`url(#${clip})`} />
            <line className="bio-hops-marker" x1={curve.indicatorX} x2={curve.indicatorX} y1={curve.indicatorY} y2={curve.baselineY} />
          </svg>
          <div className="bio-hops-ends">
            <span>{end(curve.domainMin)}</span>
            <span>{end(curve.domainMax)}</span>
          </div>
        </>
      ) : (
        <div className="bio-hops-ends">No distribution to compare with.</div>
      )}
    </div>
  );
}
